import {NodeElement, NodeElementOptions, StyleObject} from "./NodeElement";
import {PanelElement} from "./PanelElement";
import {IFrameMessages} from "../../messaging/IFrameMessages";
import {GATE_MOUNT_TYPE} from "../../Constants";

export interface InteractiveHandlers {
    remove: () => void;
    updatePanel: (panelOptions: Partial<NodeElementOptions>) => void;
}

export const GateStyle = {
    gate: {
        pointerEvents: "initial",
        height: "100%",
        display: "flex"
    },
};

export class GateElement extends NodeElement {
    styleSheet = GateStyle;
    panel: PanelElement;
    parentNode: HTMLElement;
    initialParentStyle: Record<string, string>;

    constructor(options: NodeElementOptions, panel: PanelElement) {
        super(options);
        this.panel = panel;
    }

    refreshStyle(): void {
        super.refreshStyle();
        this.setStyle(this.styleSheet.gate);
        Object.assign(this.parentNode.style, this.computeParentStyle());
        this.panel.updateOptions({style: this.getPanelStyle()});
        this.setStyle(this.options.customStyle ?? {});
    }

    getPanelStyle(): StyleObject {
        return {
            maxWidth: "100%",
            minWidth: "100%",
            border: 0,
            height: 0,
        };
    }

    computeParentStyle(): StyleObject {
        return {};
    }

    mount(parentNode: HTMLElement) {
        this.initialParentStyle = {};
        for (const key of Array.from(parentNode.style)) {
            this.initialParentStyle[key] = parentNode.style.getPropertyValue(key);
        }

        this.parentNode = parentNode;
        if (this.options.mountType === GATE_MOUNT_TYPE.replaceChildren) {
            parentNode.innerHTML = "";
            super.mount(parentNode, 0);
        } else if (this.options.mountType === GATE_MOUNT_TYPE.firstChild) {
            super.mount(parentNode, 0);
        } else if (this.options.mountType === GATE_MOUNT_TYPE.lastChild) {
            super.mount(parentNode, parentNode.childNodes.length);
        }

        if (this.options.gateType) {
            this.node.classList.add("blink-" + this.options.gateType);
        }

        this.panel.mount(this.node);
    }

    destroyNode(): void {
        this.panel.destroyNode();
        const revertedParentStyle: Record<string, string> = {};
        for (const key of Object.keys(this.computeParentStyle())) {
            revertedParentStyle[key] = "";
        }
        Object.assign(this.parentNode.style, revertedParentStyle, this.initialParentStyle);
        super.destroyNode();
    }

    getInteractiveHandlers(): InteractiveHandlers {
        return {
            remove: () => {
                if (!this.node) {
                    console.error("Panel has already been destroyed.");
                    return;
                }
                this.destroyNode();
            },
            updatePanel: (panelOptions: Partial<NodeElementOptions>) => {
                if (!this.panel) {
                    console.error("Panel has already been destroyed.");
                    return;
                }
                this.panel.updateOptions(panelOptions);
            }
        }
    }

    onMount(): void {
        super.onMount();
        this.panel.sizeChange.addListener(() => this.refreshStyle());
        this.panel.addSdkListener(IFrameMessages.DESTROY_PANEL, () => {
            this.destroyNode();
        });

        this.attachEventListener(document, "visibilitychange", () => {
            if (document.visibilityState === "visible") {
                this.refreshStyle();
            }
        });
    }
}
